import { delay } from '@packages/shared';
import { confirmSignIn, signIn } from 'aws-amplify/auth';
import { useState } from 'react';

export const useLogin = () => {
  const [mobile, setMobile] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [userVisibleError, setUserVisibleError] = useState<string>('');

  const handleOnMobileChange = (text: string) => {
    setMobile(text.replace(/[^0-9]/g, ''));
  };

  const handleCodeChange = (text: string) => {
    setCode(text.replace(/[^0-9]/g, ''));
  };

  const sendCode = async (mobile: string, restaurantUuid?: string) => {
    setUserVisibleError('');
    setIsLoading(true);
    try {
      if (!isMobileNumberIsValid(mobile)) {
        throw new Error('Invalid mobile number.\nPlease use format 04xxxxxxxx.');
      }

      const intMobile = convertMobileToIntFormat(mobile);
      const username = restaurantUuid ? `${restaurantUuid}_${intMobile}` : intMobile;
      console.log(`Sending SMS code to: ${intMobile}...`);

      const { nextStep } = await signIn({
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
        },
        username,
      });
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        setShowCodeInput(true);
      }
    } catch (error: any) {
      console.error('sendCode error:', error);
      if (typeof error === 'string') {
        setUserVisibleError(error.toString());
      }
      if (typeof error?.message === 'string') {
        setUserVisibleError(error?.message.toString());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (code: string) => {
    setIsLoading(true);
    try {
      const { nextStep } = await confirmSignIn({ challengeResponse: code });
      if (nextStep.signInStep === 'DONE') {
        console.log('[auth:cognito] User signed in successfully');
        // Redirect or update app state
      } else {
        setCode('');
        throw new Error('Invalid code. Please try again.');
      }
    } catch (error: any) {
      console.error('verifyCode error:', error);
      if (typeof error === 'string') {
        setUserVisibleError(error.toString());
      }
      if (typeof error?.message === 'string') {
        if (error?.message === 'DefineAuthChallenge failed with error Invalid OTP.') {
          resetUi();
          setUserVisibleError('Max failed attempts reached.');
        } else {
          setUserVisibleError(error?.message.toString());
        }
      }
    } finally {
      await delay(2000); // Wait 2 seconds for Convex server auth to have authenticated
      setIsLoading(false);
    }
  };

  const resetUi = () => {
    setShowCodeInput(false);
    setMobile('');
    setCode('');
  };

  /**
   * ONLY used by Admin user that login with a password.
   * (also used to sign in test users programmatically)
   * @param username
   * @param password
   */
  const signInWithPassword = async (username: string, password: string) => {
    setUserVisibleError('');
    try {
      await signIn({ username, password });
    } catch (e: any) {
      if (typeof e === 'string') {
        setUserVisibleError(e);
      } else if (e?.message && typeof e.message === 'string') {
        setUserVisibleError(e.message);
      } else {
        console.error(e);
      }
    }
  };

  return {
    mobile,
    code,
    isLoading,
    showCodeInput,
    userVisibleError,
    handleOnMobileChange,
    handleCodeChange,
    sendCode,
    verifyCode,
    resetUi,
    signInWithPassword,
  };
};

const isMobileNumberIsValid = (mobile: string): boolean => {
  if (mobile.length !== 10) {
    return false;
  }
  return mobile.substring(0, 2) === '04';
};

const convertMobileToIntFormat = (mobile: string): string => {
  if (mobile.substring(0, 2) === '04') {
    return `+614${mobile.substring(2)}`;
  }
  return '';
};
